import { LOCAL_AGENT_PRESENCE_PORT } from './hooks/useLocalAgentPresence';

export const LOCAL_AGENT_UPDATE_URL = `http://127.0.0.1:${LOCAL_AGENT_PRESENCE_PORT}/v1/update`;

export type LocalAgentUpdateResult = {
  ok: boolean;
  version?: string;
  error?: string;
  /** Agent is not listening on this computer. */
  missing?: boolean;
  /** Agent is old and has no POST /v1/update yet. */
  unsupported?: boolean;
};

type UpdateBody = {
  gateway?: string;
  token?: string;
  label?: string;
};

/**
 * Ask the agent on this computer to download the gateway package and restart.
 * Only works when `nextcompli-agent run` is exposing loopback presence.
 */
export async function requestLocalAgentUpdate(
  overrides: UpdateBody = {},
): Promise<LocalAgentUpdateResult> {
  if (typeof window === 'undefined') {
    return { ok: false, error: 'not in browser', missing: true };
  }
  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 120_000);
    const response = await fetch(LOCAL_AGENT_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(overrides),
      signal: controller.signal,
      cache: 'no-store',
    });
    window.clearTimeout(timer);
    if (response.status === 404) {
      return {
        ok: false,
        unsupported: true,
        error: 'This agent build cannot self-update yet. Run the Install agent command once.',
      };
    }
    const body = await response.json().catch(() => ({})) as {
      ok?: boolean;
      version?: string;
      error?: string;
      message?: string;
    };
    if (!response.ok || body.ok === false) {
      return {
        ok: false,
        error: String(body.error || body.message || `HTTP ${response.status}`),
      };
    }
    return { ok: true, version: String(body.version || '').trim() || undefined };
  } catch (error) {
    const name = error instanceof Error ? error.name : '';
    const message = error instanceof Error ? error.message : String(error || '');
    if (name === 'AbortError') {
      return { ok: false, error: 'Update timed out' };
    }
    // Failed to fetch → nothing on loopback (or CORS on a very old build).
    return {
      ok: false,
      missing: true,
      error: message || 'No agent on this computer',
    };
  }
}
